/**
 * Calendars — the org-level calendar-delegation consent screen (JIT-15819). An admin grants 8x8
 * access to the organization's meeting-room calendars ONCE here (Microsoft app consent / Google
 * DWD); after that every room's picker shows the full org room list. Without it, only rooms
 * someone personally shared appear (the per-room card links here to explain that).
 *
 * All calls are tenant-scoped server-side (the BFF forwards the operator's cid), so this screen
 * needs no room/tenant input. Buttons fail loud until the enterprise apps are provisioned
 * (Phase 0) — the same honest gating as the rest of the calendar integration.
 */

import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import Button from '@8x8/oxygen-button';
import Tag from '@8x8/oxygen-tag';
import TextField from '@8x8/oxygen-text-field';
import { Spinner } from '@8x8/oxygen-loaders';
import {
    enrollGoogleCalendar,
    getMsConsentUrl,
    getOrgCalendarStatus,
    type OrgCalendarStatus
} from '../api';
import { useToast } from '../toast';
import { Card, CardTitle, FieldHint, PageTitle, Skel } from '../ui/primitives';

const Page = styled.div`
    padding: 32px 24px 48px;
    max-width: 720px;
    margin: 0 auto;
`;

const Lead = styled.p`
    font-size: 14px;
    line-height: 20px;
    color: ${p => p.theme.textColor02};
    margin: 8px 0 24px;
`;

const StatusRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 20px;
    font-size: 13px;
    color: ${p => p.theme.textColor02};
`;

const Provider = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 16px;

    & > button {
        align-self: flex-start;
    }
`;

const ProviderHead = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 15px;
    font-weight: 600;
    color: ${p => p.theme.textColor01};
`;

const ProviderBody = styled.p`
    font-size: 13px;
    line-height: 19px;
    color: ${p => p.theme.textColor02};
    margin: 0;
`;

// Google's brand mark isn't in Oxygen; a tiny inline SVG (same approach as the sync8 picker).
function GoogleGlyph(): JSX.Element {
    return (
        <svg width="16" height="16" viewBox="0 0 20 20" aria-hidden="true">
            <path fill="#4285F4" d="M19.6 10.23c0-.68-.06-1.33-.17-1.96H10v3.71h5.38a4.6 4.6 0 0 1-2 3.02v2.51h3.24c1.9-1.75 2.98-4.33 2.98-7.28z" />
            <path fill="#34A853" d="M10 20c2.7 0 4.96-.9 6.62-2.43l-3.24-2.51c-.9.6-2.05.96-3.38.96-2.6 0-4.8-1.76-5.59-4.12H1.07v2.59A10 10 0 0 0 10 20z" />
            <path fill="#FBBC05" d="M4.41 11.9a6 6 0 0 1 0-3.8V5.51H1.07a10 10 0 0 0 0 8.98l3.34-2.59z" />
            <path fill="#EA4335" d="M10 3.98c1.47 0 2.79.5 3.83 1.5l2.87-2.87A10 10 0 0 0 10 0 10 10 0 0 0 1.07 5.51l3.34 2.59C5.2 5.74 7.4 3.98 10 3.98z" />
        </svg>
    );
}

function MicrosoftGlyph(): JSX.Element {
    return (
        <svg width="15" height="15" viewBox="0 0 18 18" aria-hidden="true">
            <path fill="#F25022" d="M0 0h8.5v8.5H0z" />
            <path fill="#7FBA00" d="M9.5 0H18v8.5H9.5z" />
            <path fill="#00A4EF" d="M0 9.5h8.5V18H0z" />
            <path fill="#FFB900" d="M9.5 9.5H18V18H9.5z" />
        </svg>
    );
}

const STATUS_TAG: Record<OrgCalendarStatus['status'], { variant: 'blue' | 'yellow' | 'light'; label: string }> = {
    active: { variant: 'blue', label: 'Connected' },
    needs_reauth: { variant: 'yellow', label: 'Needs re-authorization' },
    revoked: { variant: 'light', label: 'Disconnected' },
    none: { variant: 'light', label: 'Not connected' }
};

export function Calendars(): JSX.Element {
    const toast = useToast();
    const [status, setStatus] = useState<OrgCalendarStatus | null | undefined>(undefined); // undefined = loading
    const [gSubject, setGSubject] = useState('');
    const [gBusy, setGBusy] = useState(false);
    const [msBusy, setMsBusy] = useState(false);
    const reqId = useRef(0);

    const loadStatus = (): void => {
        const id = ++reqId.current;

        void getOrgCalendarStatus().then(result => {
            if (id !== reqId.current) {
                return;
            }
            setStatus(result.ok ? result.data : null);
        });
    };

    useEffect(loadStatus, []);

    const grantMs = async (): Promise<void> => {
        setMsBusy(true);
        // Open the consent tab synchronously (a pre-opened blank tab dodges the popup blocker),
        // then point it at the minted URL. Doing consent in a NEW TAB — not this one — matters:
        // Microsoft redirects back to rosy's callback, which ends on a "you may close this window"
        // page. In a new tab the admin closes it and is already back here; a same-tab navigation
        // would strand them on rosy's page with no way back to spaces.
        const popup = window.open('', '_blank');
        const result = await getMsConsentUrl();

        setMsBusy(false);

        if (!result.ok) {
            popup?.close();
            toast.notify('error', `Couldn't start Microsoft consent: ${result.message}`);

            return;
        }

        if (popup) {
            popup.location.href = result.data.url;
        } else {
            window.open(result.data.url, '_blank');
        }
        toast.notify('success', 'Grant consent in the new tab, then return here — room access updates once it succeeds.');
    };

    const linkGoogle = async (): Promise<void> => {
        const subject = gSubject.trim();

        if (!subject) {
            toast.notify('error', 'Enter a durable admin account.');

            return;
        }

        setGBusy(true);
        const result = await enrollGoogleCalendar(subject);

        setGBusy(false);

        if (!result.ok) {
            toast.notify('error', `Couldn't link Google Workspace: ${result.message}`);

            return;
        }

        toast.notify('success', 'Google Workspace connected. Rooms will appear shortly.');
        loadStatus();
    };

    const enrolledMs = status?.provider === 'microsoft' && status.enrolled;
    const enrolledGoogle = status?.provider === 'google' && status.enrolled;
    const tag = status ? STATUS_TAG[status.status] : null;

    return (
        <Page>
            <PageTitle>Room calendars</PageTitle>
            <Lead>
                Connect your organization&apos;s calendars so 8x8 can show every meeting room&apos;s schedule on
                its in-room display. An administrator authorizes access once for the whole organization; new rooms
                then appear automatically.
            </Lead>

            <StatusRow>
                <span>Organization access</span>
                {status === undefined ? (
                    <Skel w={110} h={22} />
                ) : tag ? (
                    <Tag variant={tag.variant}>{tag.label}</Tag>
                ) : (
                    <Tag variant="red">Couldn&apos;t load</Tag>
                )}
            </StatusRow>

            <Card>
                <Provider>
                    <ProviderHead><MicrosoftGlyph /> Microsoft 365</ProviderHead>
                    <ProviderBody>
                        A Microsoft 365 Global Admin grants one-time consent so 8x8 can list and read your
                        organization&apos;s room calendars. No per-room setup.
                    </ProviderBody>
                    <Button
                        variant="secondary"
                        isDisabled={status === undefined || msBusy}
                        iconLeft={msBusy ? <Spinner size="small" /> : undefined}
                        onClick={() => void grantMs()}
                    >
                        {enrolledMs ? 'Re-authorize Microsoft 365' : 'Grant admin consent'}
                    </Button>
                </Provider>
            </Card>

            <div style={{ height: 16 }} />

            <Card>
                <CardTitle>Google Workspace</CardTitle>
                <ProviderBody>
                    In your Google Admin console, install the 8x8 Workspace app and authorize its service account
                    for calendar access (domain-wide delegation). Then enter a durable admin account for 8x8 to act
                    as — use a role or service account, not a person (if that person leaves, access breaks).
                </ProviderBody>
                <div style={{ marginTop: 14, maxWidth: 360 }}>
                    <TextField
                        label="Admin account to impersonate"
                        value={gSubject}
                        placeholder="rooms-admin@yourdomain.com"
                        fullWidth
                        isDisabled={status === undefined || gBusy}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setGSubject(e.target.value)}
                    />
                    <FieldHint>8x8&apos;s service account acts as this admin to list your rooms.</FieldHint>
                </div>
                <div style={{ marginTop: 14 }}>
                    <Button
                        variant="secondary"
                        isDisabled={status === undefined || gBusy}
                        iconLeft={gBusy ? <Spinner size="small" /> : <GoogleGlyph />}
                        onClick={() => void linkGoogle()}
                    >
                        {enrolledGoogle ? 'Re-link Google Workspace' : 'Link Google Workspace'}
                    </Button>
                </div>
            </Card>
        </Page>
    );
}
